import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { Link } from "react-router";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { useMe } from "@/auth/use-me";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { Button } from "@/components/design/Button";
import { InlineEdit } from "@/components/design/InlineEdit";
import { MonthYearSelect } from "@/components/design/MonthYearSelect";
import {
  CreateCompanyDocument,
  OrganizationalRecordAccessDocument,
  StudioCreatePersonDocument,
  StudioDuplicateCandidatesDocument,
} from "@/graphql/generated/operations";
import { SearchEntityType, ValidationError } from "@/graphql/generated/types";
import { errorClassification, userErrorMessage } from "@/lib/user-error";
import {
  personDisplayName,
  placeholderEmailPrimary,
  studioPersonInput,
  toMonthYear,
  type StudioCompanyProposal,
  type StudioEducationProposal,
  type StudioExperienceProposal,
  type StudioExpertiseProposal,
  type StudioPersonProposal,
  type StudioProposal,
  type YearMonth,
} from "@/lib/studio-proposal";
import { companyCreateVariables } from "@/pages/company/use-company-creation";
import {
  addStudioCompanyExpertises,
  createStudioCompany,
  createStudioPerson,
  fileStudioProvenance,
  studioCompanySource,
  type PersonOutcome,
  type PersonProgress,
} from "./studio-apply";

type Check =
  | { state: "running" }
  | { state: "valid" }
  | { state: "invalid"; issues: { field?: string | null; error?: ValidationError | null }[] }
  | { state: "failed"; message: string };

const ISSUE_LABELS: Record<ValidationError, { id: string; defaultMessage: string }> = {
  [ValidationError.IsMandatory]: { id: "studio.proposal.issueMandatory", defaultMessage: "{field} is missing" },
  [ValidationError.FormatError]: { id: "studio.proposal.issueFormat", defaultMessage: "{field} has an invalid format" },
  [ValidationError.AlreadyExists]: { id: "studio.proposal.issueExists", defaultMessage: "{field} is already in use" },
  [ValidationError.Other]: { id: "studio.proposal.issueOther", defaultMessage: "{field} was not accepted" },
};

function failure(intl: IntlShape, error: unknown): Check {
  return {
    state: "failed",
    message:
      errorClassification(error) === "FORBIDDEN"
        ? intl.formatMessage({
            id: "studio.proposal.forbidden",
            defaultMessage: "You are not allowed to create this record.",
          })
        : userErrorMessage(intl, error),
  };
}

// Silent while it runs and when genesis accepts: only a problem is worth a line.
function CheckLine({ check }: { check: Check }) {
  const intl = useIntl();
  if (check.state === "running" || check.state === "valid") return null;
  if (check.state === "failed") return <p className="text-smycles-label text-danger">{check.message}</p>;
  return (
    <ul className="text-smycles-label text-danger">
      {check.issues.map((issue, index) => (
        <li key={index}>
          {intl.formatMessage(ISSUE_LABELS[issue.error ?? ValidationError.Other], { field: issue.field ?? "?" })}
        </li>
      ))}
    </ul>
  );
}

// What the last "Create" said about an entry while the card is open: a refusal, or what of a created entry did not
// go through (positions or expertise genesis did not take, the provenance note). What was created is kept in the
// entry itself (`created`, `provenance`).
type EntryOutcome = { check: Check } | { rowFailures: string[]; provenanceFailure?: string };

const linkClass = "underline underline-offset-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui";

// Under a created entry's card, which itself leads to the record: what happened, and what did not go through.
function CreatedLine({ id, outcome, noted }: { id?: string; outcome?: EntryOutcome; noted: boolean }) {
  const failures = outcome && "rowFailures" in outcome ? outcome.rowFailures : [];
  const provenanceFailure = outcome && "rowFailures" in outcome ? outcome.provenanceFailure : undefined;
  return (
    <>
      <p id={id} className="text-smycles-label text-ink">
        <FormattedMessage id="studio.proposal.created" defaultMessage="Created." />{" "}
        {noted ? (
          <>
            {" "}
            <FormattedMessage id="studio.proposal.provenanceNoted" defaultMessage="Its origin is noted in its organizational record." />
          </>
        ) : null}
      </p>
      {provenanceFailure ? (
        <p className="text-smycles-label text-danger">
          <FormattedMessage
            id="studio.proposal.provenanceFailed"
            defaultMessage="Origin not noted in the organizational record: {reason}"
            values={{ reason: provenanceFailure }}
          />
        </p>
      ) : null}
      {failures.length ? (
        <ul className="text-smycles-label text-danger">
          {failures.map((reason, index) => (
            <li key={index}>
              <FormattedMessage id="studio.proposal.rowNotTaken" defaultMessage="Not taken over: {reason}" values={{ reason }} />
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function Duplicates({ name, type }: { name: string; type: SearchEntityType }) {
  const { data } = useQuery(StudioDuplicateCandidatesDocument, {
    variables: { input: { query: name, entityTypes: [type], take: 3 } },
    fetchPolicy: "network-only",
  });
  const hits = data?.globalSearch.results ?? [];
  if (hits.length === 0) return null;
  return (
    <p className="text-smycles-label text-ink">
      <FormattedMessage id="studio.proposal.possibleDuplicates" defaultMessage="Possibly already there:" />{" "}
      {hits.map((hit, index) => (
        <span key={hit.entityId}>
          {index > 0 ? ", " : null}
          <Link
            to={type === SearchEntityType.Company ? `/c/${hit.entityId}` : `/p/${hit.personId ?? hit.entityId}`}
            className={linkClass}
          >
            {hit.label}
          </Link>
        </span>
      ))}
    </p>
  );
}

const FIELD_LABELS = {
  givenName: { id: "studio.proposal.field.givenName", defaultMessage: "Given name" },
  familyName: { id: "studio.proposal.field.familyName", defaultMessage: "Family name" },
  name: { id: "studio.proposal.field.name", defaultMessage: "Name" },
  email: { id: "studio.proposal.field.email", defaultMessage: "E-mail" },
  jobTitle: { id: "studio.proposal.field.jobTitle", defaultMessage: "Job title" },
  currentEmployer: { id: "studio.proposal.field.currentEmployer", defaultMessage: "Employer" },
  city: { id: "studio.proposal.field.city", defaultMessage: "City" },
  website: { id: "studio.proposal.field.website", defaultMessage: "Website" },
  phone: { id: "studio.proposal.field.phone", defaultMessage: "Phone" },
  summary: { id: "studio.proposal.field.summary", defaultMessage: "Description" },
  employeeCount: { id: "studio.proposal.field.employeeCount", defaultMessage: "Employees" },
  experiences: { id: "studio.proposal.field.experiences", defaultMessage: "Experience" },
  expertises: { id: "studio.proposal.field.expertises", defaultMessage: "Skills" },
  educations: { id: "studio.proposal.field.educations", defaultMessage: "Education" },
  degree: { id: "studio.proposal.field.degree", defaultMessage: "Degree" },
  subject: { id: "studio.proposal.field.subject", defaultMessage: "Subject" },
  institution: { id: "studio.proposal.field.institution", defaultMessage: "Institution" },
  from: { id: "studio.proposal.field.from", defaultMessage: "From" },
  to: { id: "studio.proposal.field.to", defaultMessage: "To" },
} as const;
type FieldName = keyof typeof FIELD_LABELS;

/**
 * One value of the card: plain text at rest, a field once clicked (InlineEdit), taken over on Enter or when
 * the field is left. Only a changed value is handed on, so tabbing through the card writes nothing.
 */
function EditableText({ value, field, onCommit, multiline = false, type = "text", wrap = false }: {
  value: string;
  field: FieldName;
  onCommit?: (next: string) => void;
  multiline?: boolean;
  type?: "text" | "number" | "email" | "tel";
  wrap?: boolean;
}) {
  const intl = useIntl();
  const [draft, setDraft] = useState(value);
  // A committed change comes back as the new value; follow it (and any change from outside).
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    setSeen(value);
    setDraft(value);
  }
  if (!onCommit) return <>{value}</>;
  return (
    <InlineEdit
      value={draft}
      onChange={setDraft}
      onCommit={(next) => {
        if (next.trim() !== value) onCommit(next.trim());
      }}
      multiline={multiline}
      type={type}
      wrap={wrap}
      clearable={false}
      ariaLabel={intl.formatMessage(FIELD_LABELS[field])}
    />
  );
}

// Exactly what would be taken over, each value under its field name.
function Fields({ fields }: { fields: [FieldName, ReactNode][] }) {
  const intl = useIntl();
  if (!fields.length) return null;
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-0.5 text-smycles-label">
      {fields.map(([field, value]) => (
        <Fragment key={field}>
          <dt className="text-ink-muted">{intl.formatMessage(FIELD_LABELS[field])}</dt>
          <dd className="break-words text-ink">{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

const fromMonthYear = (value: string): YearMonth | undefined => {
  const found = /^(\d{4})(?:-(\d{2}))?/.exec(value);
  return found ? { year: Number(found[1]), ...(found[2] ? { month: Number(found[2]) } : {}) } : undefined;
};
const text = (value: string) => value.trim() || undefined;

function useWhen() {
  const intl = useIntl();
  return (value: YearMonth) =>
    value.month === undefined
      ? String(value.year)
      : intl.formatDate(Date.UTC(value.year, value.month - 1, 1), { year: "numeric", month: "short", timeZone: "UTC" });
}

/**
 * One position or education: a line of text at rest; a click opens its fields in place. A field left empty
 * drops out of the record; an end left empty means the entry is current, which the user now decides.
 */
function CvRow({ parts, start, end, place, onEdit, editor }: {
  parts: (string | undefined)[];
  start?: YearMonth;
  end?: YearMonth;
  place?: string;
  onEdit?: () => void;
  editor: ReactNode | null;
}) {
  const intl = useIntl();
  const when = useWhen();
  if (editor) return <li>{editor}</li>;
  // An education's start is optional: "today" follows a known start only, an end alone is "until", and an entry
  // without dates shows no period.
  const period = start
    ? `${when(start)} – ${end ? when(end) : intl.formatMessage({ id: "studio.proposal.today", defaultMessage: "today" })}`
    : end
      ? intl.formatMessage({ id: "studio.proposal.until", defaultMessage: "until {date}" }, { date: when(end) })
      : "";
  const meta = [period, place].filter(Boolean).join(" · ");
  const line = (
    <>
      {parts.filter(Boolean).join(" · ")}
      {meta ? <span className="text-ink-muted">{` · ${meta}`}</span> : null}
    </>
  );
  return (
    <li>
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="-mx-1 rounded px-1 text-left hover:bg-ink/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
        >
          {line}
        </button>
      ) : line}
    </li>
  );
}

// Everything local until Done, then one change: a field and the dates edited together cannot overwrite each other.
function CvEditor({ fields, start, end, onDone }: {
  fields: { field: FieldName; value?: string }[];
  start?: YearMonth;
  end?: YearMonth;
  onDone: (edited: { values: Partial<Record<FieldName, string | undefined>>; start?: YearMonth; end?: YearMonth }) => void;
}) {
  const intl = useIntl();
  const [values, setValues] = useState<Partial<Record<FieldName, string>>>(() =>
    Object.fromEntries(fields.map(({ field, value }) => [field, value ?? ""])),
  );
  const [from, setFrom] = useState(toMonthYear(start));
  const [to, setTo] = useState(toMonthYear(end));
  return (
    <div className="box-flat flex flex-col gap-2 rounded-md p-2">
      <Fields
        fields={fields.map(({ field }) => [
          field,
          <InlineEdit
            key={field}
            value={values[field] ?? ""}
            onChange={(next) => setValues((current) => ({ ...current, [field]: next }))}
            clearable={false}
            ariaLabel={intl.formatMessage(FIELD_LABELS[field])}
          />,
        ])}
      />
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1 text-smycles-label">
        <span className="text-ink-muted">{intl.formatMessage(FIELD_LABELS.from)}</span>
        <MonthYearSelect value={from} onChange={setFrom} />
        <span className="text-ink-muted">{intl.formatMessage(FIELD_LABELS.to)}</span>
        <MonthYearSelect value={to} onChange={setTo} />
      </div>
      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onDone({
            values: Object.fromEntries(Object.entries(values).map(([field, value]) => [field, text(value ?? "")])),
            start: fromMonthYear(from),
            end: fromMonthYear(to),
          })}
        >
          <FormattedMessage id="studio.proposal.done" defaultMessage="Done" />
        </Button>
      </div>
    </div>
  );
}

// Each expertise is one editable value; emptied, it drops out. The same for a person and a company.
function expertiseField(
  expertises: StudioExpertiseProposal[],
  onChange: ((next: StudioExpertiseProposal[]) => void) | undefined,
): [FieldName, ReactNode][] {
  if (!expertises.length) return [];
  return [[
    "expertises",
    <ul key="k" className="flex flex-col gap-0.5">
      {expertises.map((expertise, index) => (
        <li key={index}>
          <EditableText
            field="expertises"
            value={expertise.description}
            onCommit={onChange ? (next) => onChange(
              text(next)
                ? expertises.map((item, at) => (at === index ? { description: text(next)! } : item))
                : expertises.filter((_, at) => at !== index),
            ) : undefined}
          />
        </li>
      ))}
    </ul>,
  ]];
}

// After an edit a position or education is taken over when genesis can take it: a position needs its title and
// a start, an education a subject, an institution or a degree (subject and start are optional). The end is the
// user's to leave empty now: that is "current".
const positionProblem = (experience: StudioExperienceProposal): StudioExperienceProposal["problem"] =>
  !experience.jobTitle ? "noTitle" : !experience.start ? "noStart" : undefined;
const educationProblem = (education: StudioEducationProposal): StudioEducationProposal["problem"] =>
  !education.field && !education.degree && !education.institution ? "noContent" : undefined;

function PersonRow({ id, person, onChange: change, outcome }: {
  id?: string;
  person: StudioPersonProposal;
  onChange?: (next: StudioPersonProposal) => void;
  outcome?: EntryOutcome;
}) {
  const intl = useIntl();
  // The dry run's own id, never the one the person is created under: a check running while "Create" writes
  // would otherwise collide with the real row.
  const [checkId] = useState(() => crypto.randomUUID());
  const [check, setCheck] = useState<Check>({ state: "running" });
  const [editing, setEditing] = useState<string | null>(null);
  const [validate] = useMutation(StudioCreatePersonDocument);
  // A created person is what genesis holds now; the card no longer edits it.
  const onChange = person.created ? undefined : change;
  useEffect(() => {
    if (person.created) return;
    let live = true;
    validate({ variables: { input: studioPersonInput(person, checkId, true, placeholderEmailPrimary(undefined)) } })
      .then(({ data }) => {
        if (!live) return;
        const out = data?.createPerson;
        setCheck(
          out?.__typename === "ValidatePersonResult" && out.issues.length
            ? { state: "invalid", issues: out.issues }
            : { state: "valid" },
        );
      })
      .catch((error: unknown) => live && setCheck(failure(intl, error)));
    return () => {
      live = false;
    };
  }, [validate, person, checkId, intl]);
  const name = personDisplayName(person);
  const set = (patch: Partial<StudioPersonProposal>) => onChange?.({ ...person, ...patch });
  const commit = onChange ? (field: keyof StudioPersonProposal) => (next: string) => set({ [field]: text(next) }) : () => undefined;
  const single = (["jobTitle", "currentEmployer", "city", "email", "website", "phone"] as const)
    .filter((field) => person[field])
    .map((field): [FieldName, ReactNode] => [
      field,
      <EditableText
        key={field}
        field={field}
        value={person[field] ?? ""}
        type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
        onCommit={onChange ? commit(field) : undefined}
      />,
    ]);

  const positionRows = person.experiences.flatMap((experience, index) => {
    if (experience.problem) return [];
    const key = `x${index}`;
    const update = (patch: Partial<StudioExperienceProposal>) => {
      const next = { ...experience, ...patch };
      set({ experiences: person.experiences.map((item, at) => (at === index ? { ...next, problem: positionProblem(next) } : item)) });
    };
    return [
      <CvRow
        key={key}
        parts={[experience.jobTitle, experience.employer]}
        start={experience.start}
        end={experience.end}
        place={experience.location}
        onEdit={onChange ? () => setEditing(key) : undefined}
        editor={editing === key ? (
          <CvEditor
            fields={[
              { field: "jobTitle", value: experience.jobTitle },
              { field: "currentEmployer", value: experience.employer },
              { field: "city", value: experience.location },
            ]}
            start={experience.start}
            end={experience.end}
            onDone={({ values, start, end }) => {
              update({ jobTitle: values.jobTitle, employer: values.currentEmployer, location: values.city, start, end });
              setEditing(null);
            }}
          />
        ) : null}
      />,
    ];
  });
  const educationRows = person.educations.flatMap((education, index) => {
    if (education.problem) return [];
    const key = `e${index}`;
    const update = (patch: Partial<StudioEducationProposal>) => {
      const next = { ...education, ...patch };
      set({ educations: person.educations.map((item, at) => (at === index ? { ...next, problem: educationProblem(next) } : item)) });
    };
    return [
      <CvRow
        key={key}
        parts={[education.degree, education.field, education.institution]}
        start={education.start}
        end={education.end}
        place={education.city}
        onEdit={onChange ? () => setEditing(key) : undefined}
        editor={editing === key ? (
          <CvEditor
            fields={[
              { field: "degree", value: education.degree },
              { field: "subject", value: education.field },
              { field: "institution", value: education.institution },
            ]}
            start={education.start}
            end={education.end}
            onDone={({ values, start, end }) => {
              update({ degree: values.degree, field: values.subject, institution: values.institution, start, end });
              setEditing(null);
            }}
          />
        ) : null}
      />,
    ];
  });
  const expertiseRow = expertiseField(person.expertises ?? [], onChange ? (expertises) => set({ expertises }) : undefined);
  const cv: [FieldName, ReactNode][] = [
    ...(positionRows.length ? [["experiences", <ul key="x" className="flex flex-col gap-0.5">{positionRows}</ul>] as [FieldName, ReactNode]] : []),
    ...(educationRows.length ? [["educations", <ul key="e" className="flex flex-col gap-0.5">{educationRows}</ul>] as [FieldName, ReactNode]] : []),
  ];
  // The name heads the card as plain text, so where the card is kept its parts are fields of their own.
  const nameFields: [FieldName, ReactNode][] = onChange
    ? [
        ["givenName", <EditableText key="givenName" field="givenName" value={person.givenName ?? ""} onCommit={commit("givenName")} />],
        ["familyName", <EditableText key="familyName" field="familyName" value={person.familyName ?? ""} onCommit={commit("familyName")} />],
      ]
    : [];
  return (
    <li>
      {/* The person card of the extended search, as the record card shows its hits: who would be created is seen as
          the person it becomes. Once created, the whole card leads to the profile. */}
      <PersonResultRow
        id={id}
        person={{ name }}
        title={name}
        to={person.created ? `/p/${person.created.id}` : null}
        description={
          <div className="flex flex-col gap-1">
            <Fields fields={[...nameFields, ...single, ...cv, ...expertiseRow]} />
            {person.created ? <CreatedLine id={id ? `${id}-created` : undefined} outcome={outcome} noted={Boolean(person.provenance)} /> : (
              <>
                <CheckLine check={outcome && "check" in outcome ? outcome.check : check} />
                <Duplicates name={name} type={SearchEntityType.Person} />
              </>
            )}
          </div>
        }
      />
    </li>
  );
}

function CompanyRow({ company, onChange: change, outcome }: {
  company: StudioCompanyProposal;
  onChange?: (next: StudioCompanyProposal) => void;
  outcome?: EntryOutcome;
}) {
  const intl = useIntl();
  const [check, setCheck] = useState<Check>({ state: "running" });
  const [validate] = useMutation(CreateCompanyDocument);
  const onChange = company.created ? undefined : change;
  useEffect(() => {
    if (company.created) return;
    let live = true;
    validate({ variables: companyCreateVariables(studioCompanySource(company), true) })
      .then(({ data }) => {
        if (!live) return;
        const out = data?.createCompany;
        setCheck(
          out?.__typename === "ValidateCompanyResult" && out.issues.length
            ? { state: "invalid", issues: out.issues }
            : { state: "valid" },
        );
      })
      .catch((error: unknown) => live && setCheck(failure(intl, error)));
    return () => {
      live = false;
    };
  }, [validate, company, intl]);
  const set = onChange ? (patch: Partial<StudioCompanyProposal>) => onChange({ ...company, ...patch }) : undefined;
  const fields: [FieldName, ReactNode][] = [];
  if (company.employeeCount !== undefined) {
    fields.push([
      "employeeCount",
      set ? (
        <EditableText
          key="employeeCount"
          field="employeeCount"
          type="number"
          value={String(company.employeeCount)}
          onCommit={(next) => {
            const count = Number.parseInt(next, 10);
            set({ employeeCount: Number.isFinite(count) && count >= 0 ? count : undefined });
          }}
        />
      ) : intl.formatNumber(company.employeeCount),
    ]);
  }
  if (company.summary) {
    fields.push([
      "summary",
      <EditableText key="summary" field="summary" multiline value={company.summary} onCommit={set ? (next) => set({ summary: text(next) }) : undefined} />,
    ]);
  }
  fields.push(...expertiseField(company.expertises ?? [], set ? (expertises) => set({ expertises }) : undefined));
  if (set) {
    fields.unshift([
      "name",
      // A company without a name is no record: an emptied name keeps the old one.
      <EditableText key="name" field="name" value={company.name} onCommit={(next) => next && set({ name: next })} />,
    ]);
  }
  return (
    <li>
      {/* The same card as a person's; once created it leads to the company page. */}
      <PersonResultRow
        person={{ name: company.name }}
        title={company.name}
        to={company.created ? `/c/${company.created.id}` : null}
        description={
          <div className="flex flex-col gap-1">
            <Fields fields={fields} />
            {company.created ? <CreatedLine outcome={outcome} noted={Boolean(company.provenance)} /> : (
              <>
                <CheckLine check={outcome && "check" in outcome ? outcome.check : check} />
                <Duplicates name={company.name} type={SearchEntityType.Company} />
              </>
            )}
          </div>
        }
      />
    </li>
  );
}

function personOutcome(intl: IntlShape, outcome: PersonOutcome, provenanceFailure?: string): EntryOutcome | undefined {
  if (outcome.ok) return outcome.rowFailures.length || provenanceFailure ? { rowFailures: outcome.rowFailures, provenanceFailure } : undefined;
  return { check: "issues" in outcome ? { state: "invalid", issues: outcome.issues } : failure(intl, outcome.error) };
}

/**
 * The Studio proposal (@external-adr AGENT-SERVICE-0036-the-studio-proposes-records-the-user-decides): only
 * what would be taken over, each value editable where the card is kept in the notebook, so the user adjusts
 * it before anything is created. Every entry is checked against genesis with a dry run and against existing
 * records by name. "Create" writes them with the user's own rights, only where the card is kept: what was created
 * stays marked there and is not created twice.
 */
export function StudioProposalCard({ id, proposal: given, onChange: persist }: { id?: string; proposal: StudioProposal; onChange?: (next: StudioProposal) => void }) {
  const intl = useIntl();
  const client = useApolloClient();
  const me = useMe();
  const mayCreatePersons = useHasPermission(Permission.WritePerson);
  const mayCreateCompanies = useHasPermission(Permission.CreateCompany);
  // Every record the card creates gets its provenance noted in its organizational record (spark #311), so creating
  // needs the right to keep those records too.
  const recordAccess = useQuery(OrganizationalRecordAccessDocument);
  const mayKeepRecords = recordAccess.data?.organizationalRecordAccess.canWrite === true;
  const [creating, setCreating] = useState(false);
  const [outcomes, setOutcomes] = useState<Record<string, EntryOutcome>>({});
  // Per person and company id, what a first click got through, so a second one continues where it stopped.
  const progress = useRef(new Map<string, PersonProgress>());
  const companyProgress = useRef(new Map<string, Set<string>>());
  // A working copy: each edit builds on the last one, even before its write to the notebook has come back.
  // It follows the notebook when that holds something else (the saved edit, a reload).
  // Compared by content, since the notebook parses a fresh object on every render; adjusted while rendering
  // rather than in an effect, so the first render already holds it and nothing is checked twice.
  const givenKey = JSON.stringify(given);
  const [proposal, setProposal] = useState(given);
  const [seenKey, setSeenKey] = useState(givenKey);
  if (givenKey !== seenKey) {
    setSeenKey(givenKey);
    setProposal(given);
  }
  const onChange = persist
    ? (next: StudioProposal) => {
        setProposal(next);
        persist(next);
      }
    : undefined;
  const empty = !proposal.persons.length && !proposal.companies.length;
  // A created entry stays open to a second click while its provenance note is missing or some of its positions,
  // expertise or the like were refused.
  const personOpen = (person: StudioPersonProposal, index: number) =>
    !person.created || !person.provenance || Boolean(outcomes[`p${index}`]);
  const companyOpen = (company: StudioCompanyProposal, index: number) =>
    !company.created || !company.provenance || Boolean(outcomes[`c${index}`]);
  const openPersons = proposal.persons.some(personOpen);
  const openCompanies = proposal.companies.some(companyOpen);
  const canCreate = Boolean(onChange) && mayKeepRecords
    && ((mayCreatePersons && openPersons) || (mayCreateCompanies && openCompanies));
  const missingRight = !mayKeepRecords || (openPersons && !mayCreatePersons) || (openCompanies && !mayCreateCompanies);

  const create = async () => {
    if (!onChange) return;
    setCreating(true);
    // Each step builds on the last one written to the notebook, not on the render the click came from.
    let current = proposal;
    const save = (next: StudioProposal) => {
      current = next;
      onChange(next);
    };
    const setPerson = (index: number, next: StudioPersonProposal) =>
      save({ ...current, persons: current.persons.map((item, at) => (at === index ? next : item)) });
    const next: Record<string, EntryOutcome> = {};
    for (const [index, person] of current.persons.entries()) {
      if (!mayCreatePersons || !personOpen(person, index)) continue;
      // Kept before the first write, so a second attempt after a reload asks for the same person.
      const personId = person.personId ?? crypto.randomUUID();
      if (!person.personId) setPerson(index, { ...person, personId });
      const done = progress.current.get(personId) ?? { person: false, rows: new Set<string>() };
      progress.current.set(personId, done);
      const outcome = await createStudioPerson({ client, intl, person: current.persons[index]!, personId, creatorId: me.id, progress: done });
      if (outcome.ok && !current.persons[index]!.created) setPerson(index, { ...current.persons[index]!, created: { id: personId } });
      let provenanceFailure: string | undefined;
      if (outcome.ok && !current.persons[index]!.provenance) {
        const noted = await fileStudioProvenance({
          client, intl, subject: { kind: "person", id: personId }, proposal: current, sourceNoteIds: current.persons[index]!.sourceNoteIds,
        });
        if (noted.ok) setPerson(index, { ...current.persons[index]!, provenance: { entryId: noted.entryId } });
        else provenanceFailure = noted.message;
      }
      const shown = personOutcome(intl, outcome, provenanceFailure);
      if (shown) next[`p${index}`] = shown;
    }
    for (const [index, company] of current.companies.entries()) {
      if (!mayCreateCompanies || !companyOpen(company, index)) continue;
      let companyId = company.created?.id;
      if (!companyId) {
        const outcome = await createStudioCompany(client, intl, company);
        if (!outcome.ok) {
          next[`c${index}`] = { check: { state: "failed", message: outcome.message } };
          continue;
        }
        companyId = outcome.id;
        save({ ...current, companies: current.companies.map((item, at) => (at === index ? { ...item, created: { id: companyId! } } : item)) });
      }
      const done = companyProgress.current.get(companyId) ?? new Set<string>();
      companyProgress.current.set(companyId, done);
      const failures = await addStudioCompanyExpertises({ client, intl, companyId, company: current.companies[index]!, done });
      let provenanceFailure: string | undefined;
      if (!current.companies[index]!.provenance) {
        const noted = await fileStudioProvenance({
          client, intl, subject: { kind: "company", id: companyId }, proposal: current, sourceNoteIds: current.companies[index]!.sourceNoteIds,
        });
        if (noted.ok) {
          save({ ...current, companies: current.companies.map((item, at) => (at === index ? { ...item, provenance: { entryId: noted.entryId } } : item)) });
        } else {
          provenanceFailure = noted.message;
        }
      }
      if (failures.length || provenanceFailure) next[`c${index}`] = { rowFailures: failures, provenanceFailure };
    }
    setOutcomes(next);
    setCreating(false);
  };
  return (
    <section id={id} className="box-flat mt-2 flex flex-col gap-3 rounded-lg p-3">
      <h3 className="text-smycles-meta font-medium text-ink">
        <FormattedMessage id="studio.proposal.title" defaultMessage="Add from the notebook" />
      </h3>
      {empty ? (
        <p className="text-smycles-label text-ink">
          <FormattedMessage id="studio.proposal.nothing" defaultMessage="Nothing here is backed by the notebook, so there is nothing to take over." />
        </p>
      ) : (
        <>
          {proposal.persons.length ? (
            <div className="flex flex-col gap-1.5">
              <h4 className="smycles-field-label">
                <FormattedMessage id="studio.proposal.persons" defaultMessage="People" />
              </h4>
              <ul className="grid grid-cols-1 gap-3 p-0.5 @sm:grid-cols-2">
                {proposal.persons.map((person, index) => (
                  <PersonRow
                    key={`p${index}`}
                    id={id ? `${id}-p${index}` : undefined}
                    person={person}
                    outcome={outcomes[`p${index}`]}
                    onChange={onChange ? (next) => onChange({ ...proposal, persons: proposal.persons.map((item, at) => (at === index ? next : item)) }) : undefined}
                  />
                ))}
              </ul>
            </div>
          ) : null}
          {proposal.companies.length ? (
            <div className="flex flex-col gap-1.5">
              <h4 className="smycles-field-label">
                <FormattedMessage id="studio.proposal.companies" defaultMessage="Companies" />
              </h4>
              <ul className="grid grid-cols-1 gap-3 p-0.5 @sm:grid-cols-2">
                {proposal.companies.map((company, index) => (
                  <CompanyRow
                    key={`c${index}`}
                    company={company}
                    outcome={outcomes[`c${index}`]}
                    onChange={onChange ? (next) => onChange({ ...proposal, companies: proposal.companies.map((item, at) => (at === index ? next : item)) }) : undefined}
                  />
                ))}
              </ul>
            </div>
          ) : null}
          {openPersons || openCompanies ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button id={id ? `${id}-create` : undefined} variant="primary" size="compact" disabled={!canCreate || creating} onClick={() => void create()}>
                {creating ? (
                  <FormattedMessage id="studio.proposal.creating" defaultMessage="Creating…" />
                ) : (
                  <FormattedMessage id="studio.proposal.create" defaultMessage="Create" />
                )}
              </Button>
              {/* Why the button waits, where it waits: a greyed button alone does not say it. */}
              {!onChange ? (
                <span className="text-smycles-label text-ink-muted">
                  <FormattedMessage id="studio.proposal.keepToCreate" defaultMessage="Add the card to the notebook to create it." />
                </span>
              ) : missingRight ? (
                <span className="text-smycles-label text-ink-muted">
                  {!mayKeepRecords ? (
                    <FormattedMessage id="studio.orgRecord.noRight" defaultMessage="You may not keep organizational records." />
                  ) : openPersons && !mayCreatePersons ? (
                    <FormattedMessage id="studio.proposal.noPersonRight" defaultMessage="You may not create people." />
                  ) : (
                    <FormattedMessage id="studio.proposal.noCompanyRight" defaultMessage="You may not create companies." />
                  )}
                </span>
              ) : (
                <span className="text-smycles-label text-ink-muted">
                  <FormattedMessage
                    id="studio.proposal.createHint"
                    defaultMessage="Creates them and notes their origin in each one's organizational record."
                  />
                </span>
              )}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
